import { useCallback, useMemo } from 'react';

import {
  useAvailableClientStatusesLazy,
  useSaveClientOnboardingStatus,
} from 'api/requests';
import { useSaveClientAccountStatus } from 'api/requests/client';
import {
  ClientAccountStatusType,
  ClientEntity,
  ClientOnboardingStatusType,
  OnboardingStatusType,
  PermissionType,
} from 'api/types/entity';
import { useServerErrorNotify } from 'hooks';
import { useTranslation } from 'libs/i18n';
import { useNotify } from 'libs/notify';
import { usePermissions } from 'libs/permissions';
import { useNotifySuccessSave } from 'modules/client/common/hooks';

import { filterVisibleOnboardingStatuses } from '../../common/helpers/filterVisibleOnboardingStatuses';

export const useClientStatus = (client: ClientEntity | null) => {
  const notifySuccessSave = useNotifySuccessSave();
  const notify = useNotify();
  const { t } = useTranslation();
  const { checkPermissions } = usePermissions();

  const clientId = client?.id;
  const {
    availableStatuses,
    loading: fetchStatusesLoading,
    getAvailableClientStatuses,
  } = useAvailableClientStatusesLazy();

  const {
    loading: saveClientOnboardingStatusLoading,
    saveClientOnboardingStatus,
    error: saveClientOnboardingStatusError,
  } = useSaveClientOnboardingStatus();

  const {
    error: saveClientAccountStatusError,
    loading: saveClientAccountStatusLoading,
    saveClientAccountStatus,
  } = useSaveClientAccountStatus();

  const visibleOnboardingStatuses = useMemo(
    () => filterVisibleOnboardingStatuses(client?.onboarding?.statuses),
    [client?.onboarding?.statuses]
  );

  const statuses = useMemo(
    () => ({
      onboarding: availableStatuses?.onboarding ?? [],
      account: availableStatuses?.account ?? [],
    }),
    [availableStatuses?.account, availableStatuses?.onboarding]
  );

  const fetchAvailableStatuses = useCallback(() => {
    if (clientId) getAvailableClientStatuses(clientId);
  }, [getAvailableClientStatuses, clientId]);

  const onChangeOnboardingStatus = checkPermissions([
    PermissionType.Client_LegalClient_SaveOnboardingStatus,
  ])
    ? async (value: ClientOnboardingStatusType, result?: string) => {
        const updatedClient = await saveClientOnboardingStatus(clientId!, {
          value,
          result,
          versionNumber: client?.versionNumber!,
        });

        if (updatedClient) {
          notifySuccessSave(value, 'create', updatedClient.isBrightSyncEnabled);

          if (
            value === OnboardingStatusType.AccountOpened &&
            updatedClient?.personalAccount.number &&
            updatedClient?.personalAccount.isCreatedBySystem
          ) {
            notify.info(
              t('client.client.statuses.personalAccountOpenedNotifyText')
            );
          }
        }
      }
    : undefined;

  const onChangeAccountStatus = checkPermissions([
    PermissionType.Client_LegalClient_SaveAccountStatus,
  ])
    ? async (value: ClientAccountStatusType) => {
        const updatedClient = await saveClientAccountStatus(clientId!, {
          value,
          versionNumber: client?.versionNumber!,
        });

        if (updatedClient) {
          notifySuccessSave();
        }
      }
    : undefined;

  useServerErrorNotify(saveClientOnboardingStatusError);
  useServerErrorNotify(saveClientAccountStatusError);

  return {
    statuses,
    loading: fetchStatusesLoading,
    submitLoading:
      saveClientOnboardingStatusLoading || saveClientAccountStatusLoading,
    fetchAvailableStatuses,
    onChangeOnboardingStatus,
    visibleOnboardingStatuses,
    onChangeAccountStatus,
  };
};
